<template>
  <section class="old-search">
    <div class="old-search__total-results-wrapper">
      {{ searchLabel }}
    </div>
    <div class="old-search__breadcrumbs-wrapper">
      <Breadcrumbs
        :currentContext="currentContext"
        :currentRefinements="currentRefinements"
        :filterMap="filterMap"
        :features="features"
        :searchQuery="searchQuery"
        @remove-filter="removeFilterAndSearch"
        @clear-filter="clearFilterAndSearch"
      />
    </div>
    <div class="old-search__toggles-wrapper">
      <Toggles :viewStyle="viewStyle" @update-view-style="updateViewStyle" />
    </div>
    <div class="old-search__filters-wrapper">
      <Filters
        :currentContext="currentContext"
        :features="features"
        :filters="currentFilters"
        :filterMap="filterMap"
        :currentRefinedValues="currentRefinedValues"
        :isFreeTrialUser="isFreeTrialUser"
        :totalResults="totalResults"
        @update-filters="updateFilterAndSearch"
        @clear-filter="clearFilterAndSearch"
      />
    </div>
    <div class="old-search__results-wrapper">
      <div class="old-search__context-wrapper">
        <Context
          :contexts="contexts"
          :currentContext="currentContext"
          :itemsLength="itemsLength"
          @update-context="updateContext"
        />
      </div>
      <template v-if="!noResultsFound">
        <TileResults
          v-if="viewStyle === 'tile'"
          :items="currentItems"
          :contentType="currentContext"
          :isFreeTrialUser="isFreeTrialUser"
          :parentsSearchResults="parentsSearchResults"
          :activeCourses="activeCourses"
          @open-video="openVideo"
          @element-rendered="handleRememberPosition"
        />
        <ListResults
          v-else
          :items="currentItems"
          :contentType="currentContext"
          :isFreeTrialUser="isFreeTrialUser"
          :parentsSearchResults="parentsSearchResults"
          :activeCourses="activeCourses"
          @open-video="openVideo"
          @element-rendered="handleRememberPosition"
        />
      </template>
      <div v-else class="old-search__no-results-wrapper">
        <h6 class="old-search__no-results">No Results Found</h6>
      </div>
    </div>
    <SingleVideoWithContext
      v-if="videoItem"
      :video="videoItem"
      trigger="search"
      :parents="parentsSearchResults"
      @close-video-player="closeVideo"
    />
  </section>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { throttle } from 'lodash'
import {
  isBottomVisible,
  cleanSearchStorage,
  getSearchStorage,
  saveSearchContext,
  saveCurrentPage,
  cleanSearchPage,
  saveSearchViewStyle,
} from '@/helpers'
import {
  createAlgoliaIndices,
  toggleAlgoliaFacet,
  clearAlgoliaFacets,
} from '@/utils'
import Filters from '@/components/search/Filters.vue'
import TileResults from '@/components/search/TileResults.vue'
import ListResults from '@/components/search/ListResults.vue'
import Context from '@/components/search/Context.vue'
import Breadcrumbs from '@/components/search/Breadcrumbs.vue'
import Toggles from '@/components/shared/Toggles.vue'
import SingleVideoWithContext from '@/components/media/SingleVideoWithContext.vue'
import CONSTANTS from '@/constants'

export default {
  name: 'Search',
  components: {
    Filters,
    TileResults,
    ListResults,
    Context,
    Breadcrumbs,
    Toggles,
    SingleVideoWithContext,
  },
  metaInfo() {
    return {
      title: `INE - Search`,
      meta: [
        {
          vmid: 'robots',
          name: 'robots',
          content: 'noindex',
        },
      ],
    }
  },
  data() {
    return {
      hasReachedBottom: false,
      searchInstance: {},
      indices: {
        Courses: {},
        'Learning Paths': {},
        Videos: {},
        Bootcamps: {},
        Labs: {},
      },
      searchResults: {
        Courses: [],
        'Learning Paths': [],
        Videos: [],
        Bootcamps: [],
        Labs: [],
      },
      pages: {
        Courses: {
          currentPage: 0,
          totalPages: 0,
          totalResults: 0,
        },
        'Learning Paths': {
          currentPage: 0,
          totalPages: 0,
          totalResults: 0,
        },
        Videos: {
          currentPage: 0,
          totalPages: 0,
          totalResults: 0,
        },
        Bootcamps: {
          currentPage: 0,
          totalPages: 0,
          totalResults: 0,
        },
        Labs: {
          currentPage: 0,
          totalPages: 0,
          totalResults: 0,
        },
      },
      facetResults: {
        Courses: {},
        'Learning Paths': {},
        Videos: {},
        Bootcamps: {},
        Labs: {},
      },
      currentRefinements: {
        Courses: {},
        'Learning Paths': {},
        Videos: {},
        Bootcamps: {},
        Labs: {},
      },
      currentContext: getSearchStorage().searchContext
        ? getSearchStorage().searchContext
        : 'Courses',
      viewStyle: getSearchStorage().viewStyle
        ? getSearchStorage().viewStyle
        : 'tile',
      videoItem: null,
      hasUpdatedStatus: false,
      videosParents: {
        Courses: {},
        'Learning Paths': {},
        Videos: {},
        Bootcamps: {},
        Labs: {},
      },
      parentsSearchResults: {
        Courses: {},
        'Learning Paths': {},
        Bootcamps: {},
      },
      searchStorage: getSearchStorage(),
    }
  },
  computed: {
    ...mapState({
      algoliaKeys: state => state.search.algoliaKeys,
      searchQuery: state => state.search.searchQuery,
      contexts: state => state.search.contexts,
      filterMap: state => state.search.filterMap,
      features: state => state.search.features,
      facets: state => state.search.facets,
      isAuthenticated: state => state.auth.isAuthenticated,
      profile: state => state.auth.userProfile,
      activeCourses: state => state.courses.activeCourses,
    }),
    ...mapGetters({
      isFreeTrialUser: 'auth/isFreeTrialUser',
    }),
    currentItems() {
      return this.searchResults[this.currentContext]
    },
    itemsLength() {
      return {
        Courses: this.searchResults.Courses.length,
        'Learning Paths': this.searchResults['Learning Paths'].length,
        Videos: this.searchResults.Videos.length,
        Bootcamps: this.searchResults.Bootcamps.length,
        Labs: this.searchResults.Labs.length,
      }
    },
    currentFilters() {
      return this.facetResults[this.currentContext]
    },
    noResultsFound() {
      return this.itemsLength[this.currentContext] === 0
    },
    currentRefinedValues() {
      return Object.entries(this.currentRefinements[this.currentContext]).map(
        (value, _) => value
      )
    },
    currentContextResultslength() {
      return this.itemsLength[this.currentContext]
    },
    totalResults() {
      return this.pages[this.currentContext].totalResults
    },
    searchLabel() {
      const searchQueryLabel = this.searchQuery
        ? ` for ${this.searchQuery}`
        : ''
      return `${this.totalResults} results ${searchQueryLabel}`
    },
  },
  watch: {
    searchQuery: {
      handler(query) {
        this.handleSearchQuery(query)
      },
      immediate: false,
    },
    hasReachedBottom(bottom) {
      if (bottom) {
        this.loadMore()
      }
    },
    currentItems(newItems, oldItems) {
      if (
        this.searchStorage.searchCurrentPage &&
        this.pages[this.currentContext].currentPage <
          this.searchStorage.searchCurrentPage &&
        this.pages[this.currentContext].currentPage <
          this.pages[this.currentContext].totalPages
      ) {
        this.loadMore()
        this.searchStorage = { ...this.searchStorage, currentPage: 0 }
      }
    },
  },
  beforeDestroy() {
    saveCurrentPage(this.pages[this.currentContext].currentPage)
    window.removeEventListener('scroll', this.handleScroll)
  },
  created() {
    this.createAlgoliaSearchInstance()
    this.handleUrlParams()
    this.handleScroll = throttle(() => {
      this.hasReachedBottom = isBottomVisible()
    }, 50)
    window.addEventListener('scroll', this.handleScroll)
  },
  mounted() {
    this.$store.dispatch('courses/getActiveCourses')
  },
  methods: {
    handleRememberPosition(elId) {
      if (elId === this.searchStorage.searchTile) {
        setTimeout(() => {
          this.$scrollTo(`#${elId}`, 1000, {
            container: 'body',
            offset: -150,
          })
          cleanSearchStorage()
          this.searchStorage = { ...this.searchStorage, searchTile: '' }
        }, 1000)
      }
    },
    updateViewStyle(style) {
      saveSearchViewStyle(style)
      this.viewStyle = style
    },
    loadMore() {
      const { currentPage, totalPages } = this.pages[this.currentContext]
      if (currentPage !== totalPages) {
        this.indices[this.currentContext].nextPage()
        this.indices[this.currentContext].search()
      }
    },
    buildUrlParams(facets) {
      const params = new URLSearchParams('')
      if (this.searchQuery) {
        params.set('query', this.searchQuery)
      }
      Object.entries(facets).map((data, _) => {
        const title = this.filterMap[data[0]] || data[0]
        params.set(
          title,
          data[1].map(value => encodeURIComponent(value)).join(',')
        )
      })
      window.history.replaceState(
        {},
        '',
        decodeURIComponent(`${window.location.pathname}?${params}`)
      )
    },
    getCorrectTitle(title) {
      let newTitle = title
      Object.entries(this.filterMap).map((property, _) => {
        if (property[1] === title) {
          newTitle = property[0]
        }
      })
      return newTitle
    },
    handleUrlParams() {
      const params = new URLSearchParams(window.location.search)
      if (params.has('query')) {
        this.$store.dispatch('search/setQuery', params.get('query'))
      } else {
        this.handleSearchQuery(this.searchQuery)
      }
      this.applyFiltersFromParams()
    },
    cleanUrlParams() {
      this.buildUrlParams([])
    },
    applyFiltersFromParams() {
      const params = new URLSearchParams(window.location.search)
      const facets = this.currentContext
        ? this.facets[this.currentContext]
        : this.facets.Courses
      const values = {}
      facets.map(facet => {
        const title = this.filterMap[facet] || facet
        if (params.has(title)) {
          const formattedTitle = this.getCorrectTitle(title)
          values[formattedTitle] = params.getAll(title)
        }
      })
      Object.entries(values).map((entry, _) => {
        entry[1].map(facet => {
          const splitValues = facet.split(',')
          splitValues.map(value => {
            this.updateFilters({
              facet: entry[0],
              value,
            })
          })
        })
      })
      if (Object.entries(values)?.length) {
        this.indices[this.currentContext].search()
      }
    },
    handleResults(index, { hits, disjunctiveFacets, page, nbPages, nbHits }) {
      this.facetResults[index] = disjunctiveFacets
      this.pages[index].currentPage = page
      this.pages[index].totalPages = nbPages
      this.pages[index].totalResults = nbHits
      if (this.pages[index].currentPage === 0) {
        this.searchResults[index] = hits
      } else {
        this.searchResults[index] = [...this.searchResults[index], ...hits]
      }
      if (
        (index === 'Videos' || index === 'Labs') &&
        this.searchResults[index] &&
        this.searchResults[index].length
      ) {
        this.getParents(index)
      }
      this.completeHitsInfo(index)
    },
    handleSearchQuery(query) {
      // We search each context to show active tabs
      this.contexts.map(index => {
        this.indices[index].setQuery(query)
        this.indices[index].search()
      })
    },
    handleUpdates(index, { disjunctiveFacetsRefinements, page }) {
      this.currentRefinements[index] = disjunctiveFacetsRefinements
      this.pages[index].currentPage = page
      this.buildUrlParams(disjunctiveFacetsRefinements)
    },
    updateContext(context) {
      this.currentContext = context
      saveSearchContext(context)
      cleanSearchPage()
      this.cleanUrlParams()
    },
    openVideo(video) {
      if (this.isAuthenticated) {
        this.videoItem = video
      } else {
        this.$store.dispatch('app/setError', 'Please login to watch this video')
      }
    },
    closeVideo() {
      this.videoItem = null
      const elId = getSearchStorage()?.searchTile
      this.$scrollTo(`#${elId}`, 1000, {
        container: 'body',
        offset: -150,
      })
      cleanSearchStorage()
      this.searchStorage = { ...this.searchStorage, searchTile: '' }
    },
    updateFilters({ facet, value }) {
      toggleAlgoliaFacet(this.indices[this.currentContext], { facet, value })
    },
    updateFilterAndSearch({ facet, value }) {
      this.updateFilters({ facet, value })
      this.indices[this.currentContext].search()
    },
    removeFilterAndSearch({ facet, value }) {
      toggleAlgoliaFacet(this.indices[this.currentContext], { facet, value })
      this.indices[this.currentContext].search()
    },
    clearFilterAndSearch() {
      clearAlgoliaFacets(this.indices[this.currentContext])
      this.indices[this.currentContext].search()
    },
    createAlgoliaSearchInstance() {
      const {
        algoliaKeys: {
          appKey,
          appId,
          coursesIndex: courses,
          learningPathsIndex: learningPaths,
          videosIndex: videos,
          bootcampsIndex: bootcamps,
          labsIndex: labs,
        },
      } = this
      const setup = {
        keys: {
          appId,
          appKey,
        },
        contexts: [
          {
            name: 'Courses',
            index: courses,
            facets: this.facets.Courses,
          },
          {
            name: 'Learning Paths',
            index: learningPaths,
            facets: this.facets['Learning Paths'],
          },
          {
            name: 'Videos',
            index: videos,
            facets: this.facets.Videos,
          },
          {
            name: 'Bootcamps',
            index: bootcamps,
            facets: this.facets.Bootcamps,
          },
          {
            name: 'Labs',
            index: labs,
            facets: this.facets.Labs,
          },
        ],
        disjunctiveFacets: true,
        resultsHandler: this.handleResults,
        updatesHandler: this.handleUpdates,
        initWithSearch: false,
      }
      const { instance, indices } = createAlgoliaIndices(setup)
      this.searchInstance = instance
      this.indices = indices
    },
    async getParents(index) {
      // This function will find missing parents that the view needs and search in Algolia by ObjectIDs
      const parents = CONSTANTS.SEARCH.PARENTS
      const parentsIds = this.searchResults[index].reduce(
        (acc, v) => {
          if (!v.parent_id) {
            return acc
          }
          if (!acc[parents[v.parent_type]].includes(v.parent_id)) {
            // Add parent to corresponding type
            acc[parents[v.parent_type]].push(v.parent_id)
          }
          return acc
        },
        {
          Courses: [],
          'Learning Paths': [],
          Bootcamps: [],
        }
      )
      Object.keys(this.indices).map(indice => {
        if (parentsIds[indice] && parentsIds[indice].length) {
          this.indices[indice].algoliaIndex
            .getObjects(parentsIds[indice])
            .then(content => {
              this.saveParentContent(content, indice)
            })
        }
      })
    },
    saveParentContent(content, contentType) {
      this.parentsSearchResults[contentType] = content.results.reduce(
        (acc, c) => {
          // Could be a timeframe where course is deleted and index is not updated yet.
          if (c) {
            this.$set(acc, [c.objectID], c)
          }
          return acc
        },
        this.parentsSearchResults[contentType]
      )
    },
    async completeHitsInfo(index) {
      if (index === 'Bootcamps' && this.searchResults[index]?.length) {
        const bootcampsIds = this.searchResults[index].map(
          bootcamp => bootcamp.id || bootcamp.objectID
        )
        await this.$store.dispatch(
          'bootcamps/getBootcampsUserStatus',
          bootcampsIds?.join(',')
        )
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.old-search {
  background-color: var(--dark-gray);
  padding: 70px 10px;
  max-width: 1900px;
  margin: 0 auto;
  display: flex;
  flex-wrap: wrap;
  @include mobile {
    padding: 125px 25px 150px 25px;
    flex-direction: column;
  }
  @include tablet {
    flex-direction: row;
  }
  &__breadcrumbs-wrapper {
    width: 100%;
    display: none;
    position: relative;
    @include desktop {
      display: block;
    }
  }
  &__toggles-wrapper {
    display: none;
    @include mobile {
      width: 100%;
      display: flex;
      justify-content: flex-end;
      border-bottom: 1px solid var(--flat-black);
      padding: 0 0 12.4px 0;
      margin: 0 0 25px 0;
    }
  }
  &__filters-wrapper {
    width: 100%;
    padding: 0;

    @include medium-device {
      width: 25%;
      padding: 5px 15px 0 15px;
    }
  }
  &__context-wrapper {
    margin: 0 0 20px 0;
    padding: 0 0 0 5px;
    @include tablet {
      padding: 0;
    }
  }
  &__results-wrapper,
  &__no-results-wrapper {
    width: 100%;
    min-height: 100vh;
    @include medium-device {
      width: 75%;
      padding: 25px;
    }
  }
  &__no-results {
    width: 100%;
    color: var(--white);
    font-size: 1.5rem;
    text-align: center;
    margin: 0 auto;
    text-transform: uppercase;
  }
  &__total-results-wrapper {
    width: 100%;
    color: var(--white);
    font-family: var(--font-bold);
    font-size: 2em;
    margin-bottom: 10px;
    padding-left: 15px;
    @include desktop {
      display: block;
    }
  }
}
</style>
